import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Gauge, Compass, Scale, ShieldAlert, FileSearch, Layers,
  Target, Route, CheckCircle2, AlertTriangle, AlertCircle, BookOpen,
  TrendingDown, Cpu, Brain, Lightbulb
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ---------- shared helpers ----------
const statusBadge = (status) => {
  const map = {
    Pass: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    Partial: 'bg-amber-100 text-amber-800 border-amber-200',
    Fail: 'bg-red-100 text-red-800 border-red-200',
    Unanswered: 'bg-slate-100 text-slate-600 border-slate-200',
    Ready: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    'Conditionally Ready': 'bg-amber-100 text-amber-800 border-amber-200',
    'Not Ready': 'bg-red-100 text-red-800 border-red-200',
    Low: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    Medium: 'bg-amber-100 text-amber-800 border-amber-200',
    High: 'bg-red-100 text-red-800 border-red-200',
    'Ready for Submission': 'bg-emerald-100 text-emerald-800 border-emerald-200',
    'Submit with Conditions': 'bg-amber-100 text-amber-800 border-amber-200',
    'Do Not Submit': 'bg-red-100 text-red-800 border-red-200',
    Critical: 'bg-red-100 text-red-800 border-red-200',
  };
  return map[status] || 'bg-slate-100 text-slate-700 border-slate-200';
};

// Inverted scale: maturity / confidence words where 'Low' = bad and 'High' = good.
// Use only for those specific contexts (Evidence Maturity, Submission Confidence).
const maturityBadge = (level) => {
  const map = {
    Low: 'bg-red-100 text-red-800 border-red-200',
    Medium: 'bg-amber-100 text-amber-800 border-amber-200',
    High: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  };
  return map[level] || 'bg-slate-100 text-slate-700 border-slate-200';
};

const SectionHeader = ({ icon: Icon, title, subtitle, testId }) => (
  <CardHeader className="pb-2" data-testid={testId}>
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-900">
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div>
        <CardTitle className="text-xl">{title}</CardTitle>
        {subtitle && <CardDescription>{subtitle}</CardDescription>}
      </div>
    </div>
  </CardHeader>
);

// ============================================================================
// 1. EXECUTIVE SNAPSHOT
// ============================================================================
const ExecutiveSnapshot = ({ data }) => {
  const er = data.evidence_readiness_assessment;
  const erBadgeCls = !er?.assessed ? 'bg-slate-100 text-slate-600'
    : er.band?.color === 'emerald' ? 'bg-emerald-100 text-emerald-800'
    : er.band?.color === 'sky' ? 'bg-sky-100 text-sky-800'
    : er.band?.color === 'amber' ? 'bg-amber-100 text-amber-800'
    : 'bg-rose-100 text-rose-800';
  return (
  <Card className="rounded-3xl border-0 shadow-sm bg-white" data-testid="hybrid-executive-snapshot">
    <SectionHeader icon={Gauge} title="Executive Snapshot" subtitle="Board-ready DISP readiness summary" />
    <CardContent className="space-y-5">
      <div className="grid gap-4 md:grid-cols-5">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Submission Status</div>
          <Badge className={cn('text-sm px-3 py-1', statusBadge(data.submission_status))}>
            {data.submission_status}
          </Badge>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Submission Confidence</div>
          <Badge className={cn('text-sm px-3 py-1', maturityBadge(data.submission_confidence))}>
            {data.submission_confidence}
          </Badge>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Weighted ML2 Score</div>
          <div className="text-2xl font-bold text-slate-900">{data.weighted_ml2_score}%</div>
          <div className="text-xs text-slate-500">{data.score_band}</div>
        </div>
        <div data-testid="snapshot-evidence-readiness">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Evidence Readiness</div>
          <div className="text-2xl font-bold text-slate-900">
            {er?.assessed ? `${er.overall_percent}%` : '—'}
          </div>
          <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold', erBadgeCls)}>
            {er?.assessed ? er.band.label : 'Not Assessed'}
          </span>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Time to Readiness</div>
          <div className="text-lg font-semibold text-slate-900">{data.estimated_time_to_readiness}</div>
        </div>
      </div>

      <Separator />

      <div>
        <div className="text-sm font-semibold text-slate-900 mb-2">Top Business Risks</div>
        {data.top_business_risks.length === 0 ? (
          <p className="text-sm text-slate-600">No material business risks identified at this time.</p>
        ) : (
          <ul className="space-y-2">
            {data.top_business_risks.map((r, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-500" />
                <div>
                  <div className="font-medium text-slate-900">{r.title}</div>
                  <div className="text-slate-600">{r.description}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Executive Narrative</div>
        <p className="text-sm text-slate-800 leading-relaxed">{data.narrative}</p>
      </div>
    </CardContent>
  </Card>
  );
};

// ============================================================================
// 1.5 COMMERCIAL IMPACT & SUBMISSION RISK
// ============================================================================
const CommercialImpact = ({ data }) => {
  if (!data) return null;
  return (
    <Card className="rounded-3xl border-0 shadow-sm bg-white" data-testid="hybrid-commercial-impact">
      <SectionHeader icon={TrendingDown} title="Commercial Impact & Submission Risk" subtitle="Business consequences of the current control posture" />
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Contract Eligibility Delay</div>
            <div className="text-lg font-semibold text-slate-900">{data.estimated_contract_delay}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">CSQ Rejection Likelihood</div>
            <Badge className={cn('text-sm px-3 py-1', statusBadge(data.csq_rejection_likelihood))}>{data.csq_rejection_likelihood}</Badge>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Resubmission Risk</div>
            <Badge className={cn('text-sm px-3 py-1', statusBadge(data.resubmission_risk))}>{data.resubmission_risk}</Badge>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Delivery Risk</div>
            <Badge className={cn('text-sm px-3 py-1', statusBadge(data.delivery_risk))}>{data.delivery_risk}</Badge>
          </div>
        </div>
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Commercial Narrative</div>
          <p className="text-sm text-slate-800 leading-relaxed">{data.narrative}</p>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// 2. HOW TO READ THIS REPORT
// ============================================================================
const HowToRead = ({ data }) => (
  <Card className="rounded-3xl border-0 shadow-sm bg-white" data-testid="hybrid-how-to-read">
    <SectionHeader icon={BookOpen} title="How to Read This Report" subtitle="Concepts, terms, and maturity layers" />
    <CardContent>
      <div className="grid gap-4 md:grid-cols-2">
        {data.sections.map((s, i) => (
          <div key={i} className="rounded-xl bg-slate-50 border border-slate-200 p-4">
            <div className="font-semibold text-slate-900 mb-1">{s.heading}</div>
            <p className="text-sm text-slate-700 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

// ============================================================================
// 3. DECISION VIEW
// ============================================================================
const DecisionView = ({ data }) => (
  <Card className="rounded-3xl border-0 shadow-sm bg-white" data-testid="hybrid-decision-view">
    <SectionHeader icon={Scale} title="DISP Submission Decision" subtitle="Domain status, recommendation, and required actions" />
    <CardContent className="space-y-5">
      <div>
        <div className="text-sm font-semibold text-slate-900 mb-2">Domain Status</div>
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Domain</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Score</th>
                <th className="text-left px-3 py-2 font-medium">Gating Failures</th>
              </tr>
            </thead>
            <tbody>
              {data.domain_status_table.map((d) => (
                <tr key={d.domain_id} className="border-t border-slate-100" data-testid={`decision-row-${d.domain_id}`}>
                  <td className="px-3 py-2 font-medium text-slate-900">{d.domain}</td>
                  <td className="px-3 py-2">
                    <Badge className={cn('text-xs', statusBadge(d.status))}>{d.status}</Badge>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{d.percent}%</td>
                  <td className="px-3 py-2 text-slate-700">{d.gating_failures}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Separator />

      <div className={cn(
        'rounded-xl p-4 border',
        data.final_recommendation === 'Ready for Submission' && 'bg-emerald-50 border-emerald-200',
        data.final_recommendation === 'Submit with Conditions' && 'bg-amber-50 border-amber-200',
        data.final_recommendation === 'Do Not Submit' && 'bg-red-50 border-red-200'
      )}>
        <div className="flex items-center gap-3 mb-2">
          <div className="text-xs uppercase tracking-wide text-slate-600">Final Recommendation</div>
          <Badge className={cn('text-sm px-3 py-1', statusBadge(data.final_recommendation))} data-testid="decision-final-recommendation">
            {data.final_recommendation}
          </Badge>
        </div>
        <p className="text-sm text-slate-800 leading-relaxed">{data.final_recommendation_reason}</p>
      </div>

      {data.required_before_submission?.length > 0 && (
        <div>
          <div className="text-sm font-semibold text-slate-900 mb-2">Required Before Submission</div>
          <ol className="space-y-2 list-decimal list-inside" data-testid="decision-required-actions">
            {data.required_before_submission.map((a, i) => (
              <li key={i} className="text-sm text-slate-800">
                <span className="font-medium">{a.action}</span>
                <span className="ml-2 text-xs text-slate-500">[{a.priority?.toUpperCase()} - {a.domain}]</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </CardContent>
  </Card>
);

// ============================================================================
// 4. DOMAIN ENHANCEMENTS (per super-domain risk + control table + evidence)
// ============================================================================
const DomainEnhancements = ({ data }) => {
  const order = ['governance', 'personnel_security', 'physical_security', 'ict_security'];
  return (
    <Card className="rounded-3xl border-0 shadow-sm bg-white" data-testid="hybrid-domain-enhancements">
      <SectionHeader icon={Layers} title="Per-Domain Risk & Priority" subtitle="Submission risk, control priority, and audit-ready evidence per DISP super-domain" />
      <CardContent className="space-y-5">
        {order.map(superId => {
          const d = data[superId];
          if (!d) return null;
          return (
            <div key={superId} className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid={`hybrid-domain-${superId}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-semibold text-slate-900">{d.domain_label}</div>
                  <div className="text-xs text-slate-500">Score: {d.score_percent}% · Gating failures: {d.gating_failures}</div>
                </div>
                <div className="flex gap-2">
                  {d.risk_to_submission.is_gating_failure && (
                    <Badge className={cn('text-xs', statusBadge('Fail'))}>Gating failure</Badge>
                  )}
                  {d.risk_to_submission.will_fail_csq && (
                    <Badge className={cn('text-xs', statusBadge('High'))}>CSQ will fail</Badge>
                  )}
                  {!d.risk_to_submission.will_fail_csq && d.risk_to_submission.defence_will_clarify && (
                    <Badge className={cn('text-xs', statusBadge('Medium'))}>Defence clarification likely</Badge>
                  )}
                </div>
              </div>

              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Risk to DISP Submission</div>
                <p className="text-sm text-slate-800">{d.risk_to_submission.narrative}</p>
              </div>

              {d.top_risks?.length > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-red-700 font-semibold mb-1 flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" /> Top Risks in This Domain
                  </div>
                  <ul className="space-y-1">
                    {d.top_risks.map((r, i) => (
                      <li key={i} className="text-xs text-red-900 flex gap-2">
                        <span className="text-red-500">•</span><span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {d.priority_controls?.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-amber-800 font-semibold mb-1 flex items-center gap-1.5">
                    <Target className="h-3.5 w-3.5" /> Priority Controls to Address
                  </div>
                  <ul className="space-y-1">
                    {d.priority_controls.map((c, i) => (
                      <li key={i} className="text-xs text-amber-900 flex gap-2">
                        <span className="text-amber-600">•</span>
                        <div>
                          <span>{c.control}</span>
                          <span className="ml-2 text-[10px] text-amber-700">[weight {c.weight} · {c.status}]</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {d.consultant_insight && (
                <div className="rounded-lg border-l-4 border-slate-900 bg-slate-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1 flex items-center gap-1.5">
                    <Brain className="h-3.5 w-3.5" /> Consultant Insight
                  </div>
                  <p className="text-sm text-slate-800 italic leading-relaxed">{d.consultant_insight}</p>
                </div>
              )}

              {d.control_priority_table.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Control Priority</div>
                  <div className="overflow-hidden rounded-lg border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="text-left px-2 py-1.5 font-medium">Control</th>
                          <th className="text-left px-2 py-1.5 font-medium">Status</th>
                          <th className="text-left px-2 py-1.5 font-medium">Weight</th>
                          <th className="text-left px-2 py-1.5 font-medium">Risk</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.control_priority_table.map((c, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-2 py-1.5 text-slate-800">{c.control}{c.is_gating && <span className="ml-1 text-xs text-red-600 font-semibold">[gating]</span>}</td>
                            <td className="px-2 py-1.5"><Badge className={cn('text-xs', statusBadge(c.status))}>{c.status}</Badge></td>
                            <td className="px-2 py-1.5 text-slate-700">{c.weight}</td>
                            <td className="px-2 py-1.5"><Badge className={cn('text-xs', statusBadge(c.risk_level))}>{c.risk_level}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {d.evidence_required.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Evidence Required (audit-ready)</div>
                  <ul className="grid gap-1 md:grid-cols-2">
                    {d.evidence_required.map((e, i) => (
                      <li key={i} className="text-xs text-slate-700 flex gap-2">
                        <CheckCircle2 className="h-3 w-3 mt-0.5 flex-shrink-0 text-slate-400" />
                        <span>{e}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// 5. GATING CONTROL ANALYSIS
// ============================================================================
const GatingAnalysis = ({ data }) => (
  <Card className="rounded-3xl border-0 shadow-sm bg-white" data-testid="hybrid-gating-analysis">
    <SectionHeader icon={ShieldAlert} title="Gating Control Assessment" subtitle="Controls that directly determine submission eligibility" />
    <CardContent className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-center">
          <div className="text-2xl font-bold text-emerald-700">{data.summary.pass}</div>
          <div className="text-xs uppercase tracking-wide text-emerald-700">Pass</div>
        </div>
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-center">
          <div className="text-2xl font-bold text-amber-700">{data.summary.partial}</div>
          <div className="text-xs uppercase tracking-wide text-amber-700">Partial</div>
        </div>
        <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-center">
          <div className="text-2xl font-bold text-red-700">{data.summary.fail}</div>
          <div className="text-xs uppercase tracking-wide text-red-700">Fail</div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-700">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Control</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Impact</th>
            </tr>
          </thead>
          <tbody>
            {data.gating_table.map((c, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-3 py-2 text-slate-800">{c.control}</td>
                <td className="px-3 py-2"><Badge className={cn('text-xs', statusBadge(c.status))}>{c.status}</Badge></td>
                <td className="px-3 py-2 text-slate-700">{c.impact}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-xl bg-slate-900 text-white p-4">
        <div className="text-xs uppercase tracking-wide text-slate-300 mb-1">Gating Insight</div>
        <p className="text-sm leading-relaxed">{data.insight}</p>
      </div>
    </CardContent>
  </Card>
);

// ============================================================================
// 6. EVIDENCE READINESS & DEFENSIBILITY
// ============================================================================
const EvidenceReadiness = ({ data }) => (
  <Card className="rounded-3xl border-0 shadow-sm bg-white" data-testid="hybrid-evidence-readiness">
    <SectionHeader icon={FileSearch} title="Evidence Readiness & Defensibility" subtitle="Maturity, findings, and required improvements" />
    <CardContent className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="text-xs uppercase tracking-wide text-slate-500">Evidence Maturity</div>
        <Badge className={cn('text-sm px-3 py-1', maturityBadge(data.evidence_maturity_score))}>{data.evidence_maturity_score}</Badge>
        <span className="text-xs text-slate-500">
          (Evidence multiplier: {(data.evidence_multiplier).toFixed(2)} · Policy enforcement: {(data.policy_enforcement_multiplier).toFixed(2)})
        </span>
      </div>

      <div>
        <div className="text-sm font-semibold text-slate-900 mb-2">Findings</div>
        <ul className="space-y-1">
          {data.findings.map((f, i) => (
            <li key={i} className="text-sm text-slate-800 flex gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-500" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <div className="text-sm font-semibold text-slate-900 mb-2">Required Improvements</div>
        <ol className="space-y-1 list-decimal list-inside">
          {data.required_improvements.map((r, i) => (
            <li key={i} className="text-sm text-slate-800">{r}</li>
          ))}
        </ol>
      </div>

      <div className="rounded-xl border-l-4 border-slate-900 bg-slate-50 p-4">
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Defence Lens</div>
        <p className="text-sm text-slate-800 italic">{data.defence_lens}</p>
      </div>
    </CardContent>
  </Card>
);

// ============================================================================
// 7. CSQ ENHANCEMENTS
// ============================================================================
const CsqEnhancements = ({ data }) => {
  if (!data || data.length === 0) return null;
  return (
    <Card className="rounded-3xl border-0 shadow-sm bg-white" data-testid="hybrid-csq-enhancements">
      <SectionHeader icon={Target} title="CSQ Submission Risk & Numbered Path" subtitle="Per-category risk plus numbered minimum viable submission steps" />
      <CardContent className="space-y-3">
        {data.map((b, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm font-semibold text-slate-900">
                {b.csq_domain} · {b.csq_category}
              </div>
              <div className="flex gap-2">
                {b.has_gating && <Badge className={cn('text-xs', statusBadge('Fail'))}>Gating</Badge>}
                <Badge className={cn('text-xs', statusBadge(b.submission_risk))}>{b.submission_risk} risk</Badge>
              </div>
            </div>
            <div className="text-xs text-slate-600">{b.combined_name}</div>
            {b.numbered_minimum_path.length > 0 && (
              <ol className="space-y-1">
                {b.numbered_minimum_path.map((s) => (
                  <li key={s.step} className="text-xs text-slate-800">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-white text-[10px] mr-2">{s.step}</span>
                    {s.action}
                  </li>
                ))}
              </ol>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// 8. REMEDIATION ROADMAP
// ============================================================================
const RemediationRoadmap = ({ data }) => {
  const phaseStyles = {
    phase_1: { color: 'border-red-300 bg-red-50', label: 'Phase 1', dot: 'bg-red-500' },
    phase_2: { color: 'border-amber-300 bg-amber-50', label: 'Phase 2', dot: 'bg-amber-500' },
    phase_3: { color: 'border-emerald-300 bg-emerald-50', label: 'Phase 3', dot: 'bg-emerald-500' },
  };

  return (
    <Card className="rounded-3xl border-0 shadow-sm bg-white" data-testid="hybrid-remediation-roadmap">
      <SectionHeader icon={Route} title="Path to DISP Readiness" subtitle="Phased remediation roadmap" />
      <CardContent className="grid gap-4 md:grid-cols-3">
        {['phase_1', 'phase_2', 'phase_3'].map(key => {
          const phase = data[key];
          const sty = phaseStyles[key];
          if (!phase) return null;
          return (
            <div key={key} className={cn('rounded-xl border p-3', sty.color)} data-testid={`hybrid-${key}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={cn('inline-block h-2.5 w-2.5 rounded-full', sty.dot)} />
                <div className="font-semibold text-slate-900">{sty.label}</div>
                <div className="text-xs text-slate-600">· {phase.timeline}</div>
              </div>
              <div className="text-xs text-slate-700 mb-2">{phase.focus}</div>
              {phase.actions.length === 0 ? (
                <div className="text-xs text-slate-500 italic">No actions required.</div>
              ) : (
                <ul className="space-y-1">
                  {phase.actions.slice(0, 12).map((a, i) => (
                    <li key={i} className="text-xs text-slate-800 flex gap-2">
                      <span className="text-slate-400">•</span>
                      <div>
                        <div>{a.action}</div>
                        <div className="text-[10px] text-slate-500">{a.domain}{a.is_gating ? ' · gating' : ''}</div>
                      </div>
                    </li>
                  ))}
                  {phase.actions.length > 12 && (
                    <li className="text-xs text-slate-500 italic">+ {phase.actions.length - 12} more</li>
                  )}
                </ul>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// 8.5 TECHNOLOGY -> CONTROL COVERAGE MAPPING
// ============================================================================
const TechnologyControlCoverage = ({ data }) => {
  if (!data) return null;
  return (
    <Card className="rounded-3xl border-0 shadow-sm bg-white" data-testid="hybrid-technology-coverage">
      <SectionHeader icon={Cpu} title="Technology to Control Coverage" subtitle="Platform footprint mapped to the controls each addresses" />
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2">
          {data.platforms.map((p, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-3" data-testid={`tech-platform-${i}`}>
              <div className="font-semibold text-slate-900 text-sm mb-1">{p.label}</div>
              <ul className="space-y-0.5">
                {p.controls.map((c, j) => (
                  <li key={j} className="text-xs text-slate-700 flex gap-2">
                    <CheckCircle2 className="h-3 w-3 mt-0.5 flex-shrink-0 text-slate-400" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="rounded-xl bg-slate-900 text-white p-4">
          <div className="text-xs uppercase tracking-wide text-slate-300 mb-1 flex items-center gap-1.5">
            <Lightbulb className="h-3.5 w-3.5" /> Tooling Insight
          </div>
          <p className="text-sm leading-relaxed">{data.insight}</p>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// 9. FINAL RECOMMENDATION
// ============================================================================
const FinalRecommendationBlock = ({ data }) => (
  <Card className="rounded-3xl border-0 shadow-sm bg-white" data-testid="hybrid-final-recommendation">
    <SectionHeader icon={Compass} title="Final Recommendation" subtitle="Closing position aligned to the Decision View" />
    <CardContent className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <Badge className={cn('text-base px-4 py-1.5', statusBadge(data.decision))} data-testid="hybrid-final-decision">
          {data.decision}
        </Badge>
        <span className="text-sm text-slate-600">{data.reason}</span>
      </div>

      {(data.current_state || data.risk_of_proceeding || data.next_step) ? (
        <div className="grid gap-3 md:grid-cols-3 pt-2">
          {data.current_state && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="final-current-state">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">Current State</div>
              <p className="text-sm text-slate-800 leading-relaxed">{data.current_state}</p>
            </div>
          )}
          {data.risk_of_proceeding && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="final-risk-of-proceeding">
              <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold mb-1">Risk of Proceeding Now</div>
              <p className="text-sm text-amber-900 leading-relaxed">{data.risk_of_proceeding}</p>
            </div>
          )}
          {data.next_step && (
            <div className="rounded-xl border border-slate-900 bg-slate-900 p-3" data-testid="final-next-step">
              <div className="text-xs uppercase tracking-wide text-slate-300 font-semibold mb-1">Next Step</div>
              <p className="text-sm text-white leading-relaxed">{data.next_step}</p>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-800 leading-relaxed">{data.statement}</p>
      )}
    </CardContent>
  </Card>
);

// ============================================================================
// MASTER COMPONENT
// ============================================================================
export default function HybridReportSections({ hybridReport }) {
  if (!hybridReport) return null;

  return (
    <div className="space-y-6" data-testid="hybrid-report">
      <ExecutiveSnapshot data={hybridReport.executive_snapshot} />
      <CommercialImpact data={hybridReport.commercial_impact} />
      <HowToRead data={hybridReport.how_to_read} />
      <DecisionView data={hybridReport.decision_view} />
      <DomainEnhancements data={hybridReport.domain_enhancements} />
      <GatingAnalysis data={hybridReport.gating_analysis} />
      <EvidenceReadiness data={hybridReport.evidence_readiness} />
      <CsqEnhancements data={hybridReport.csq_enhancements} />
      <RemediationRoadmap data={hybridReport.remediation_roadmap} />
      <TechnologyControlCoverage data={hybridReport.technology_control_coverage} />
      <FinalRecommendationBlock data={hybridReport.final_recommendation} />
    </div>
  );
}
